import { OnDestroy, Pipe, PipeTransform } from "@angular/core";
import { DatePipe, formatNumber } from "@angular/common";

import * as moment from "moment";

import { combineLatest, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { getTimezone, loadedTenantState } from "src/app/store/reducers";

import {
  FieldType,
  FormatType,
} from "src/app/models/custom-table/custom-table.model";

@Pipe({
  name: "gridPipe",
  pure: false,
})
export class GridPipe implements PipeTransform, OnDestroy {
  tenantSub: Subscription;
  tenant$: Observable<any>;
  formatter: any;

  // locale: string = localStorage.getItem("locale");
  locale: string = 'en-en';

  constructor(
    private store: Store,
    private datePipe: DatePipe
  ) {}

  ngOnDestroy(): void {
    if (this.tenantSub) {
      this.tenantSub.unsubscribe();
    }
  }

  transform(value: any, columnConfig: any, columnElement?: any): Observable<string> {
    return combineLatest([
      this.store.select(loadedTenantState),
      this.store.select(getTimezone)
    ]).pipe(
      map(([tenant, timezone]) => {
        return this.customFormatter(value, tenant, timezone, columnConfig, columnElement);
      })
    );
  }

  customFormatter(value: any, tenant: any, timezone: string, columnConfig: any, columnElement: any): any {
    if (value !== null && value !== undefined) {
      //Date Fields
      if (
        (columnConfig?.fieldType === FieldType.DATE ||
          columnConfig?.fieldType === FieldType.DATE_TIME) &&
        columnConfig?.formatType === FormatType.DATE
      ) {
        return moment(value).format(`${tenant?.dateFormat}`).toString();
      }
      if (
        columnConfig?.fieldType === FieldType.DATE_TIME &&
        columnConfig?.formatType === FormatType.TIME
      ) {
        return timezone === 'UTC'
          ? moment.utc(value).format(tenant?.timeFormat).toString()
          : moment(value).format(tenant?.timeFormat).toString();
      }
      if (
        (columnConfig?.fieldType === FieldType.DATE ||
          columnConfig?.fieldType === FieldType.DATE_TIME) &&
        columnConfig?.formatType === FormatType.DATETIME
      ) {
        return timezone === 'UTC'
          ? moment.utc(value).format(tenant?.datetimeFormat).toString()
          : moment(value).format(tenant?.datetimeFormat).toString();
      }
      if (columnConfig?.fieldType === FieldType.DATE && Date.parse(value)) {
        return this.datePipe.transform(value);
      }

      //Numeric Fields
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.UNITPRICE
      ) {
        return formatNumber(
          value,
          this.locale,
          `${tenant?.unitPriceDecimalPlaces ? '1.' + tenant?.unitPriceDecimalPlaces + '-' + tenant?.unitPriceDecimalPlaces : '1.2-2'}`
        );
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.UOM
      ) {
        return formatNumber(
          value, 
          this.locale, 
          `${tenant?.uomDecimalPlaces ? '1.' + tenant?.uomDecimalPlaces + '-' + tenant?.uomDecimalPlaces : '1.2-2'}`
        );
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.FUELUOM
      ) {
        return formatNumber(
          value, 
          this.locale, 
          `${tenant?.forecourtUomDecimalPlaces ? '1.' + tenant?.forecourtUomDecimalPlaces + '-' + tenant?.forecourtUomDecimalPlaces : '1.2-2'}`
        );
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.FUELUNITPRICE
      ) {
        return formatNumber(
          value,
          this.locale,
          `${tenant?.forecourtUnitPriceDecimalPlaces ? '1.' + tenant?.forecourtUnitPriceDecimalPlaces + '-' + tenant?.forecourtUnitPriceDecimalPlaces : '1.3-3'}`
        );
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.AMOUNT
      ) {
        //Special case for products table (product can be fuel or not)
        if (this.checkFuelProduct(columnElement) && columnConfig.fuelFormatType) {
          return formatNumber(
            value, 
            this.locale, 
            `${tenant?.forecourtUnitPriceDecimalPlaces ? '1.' + tenant?.forecourtUnitPriceDecimalPlaces + '-' + tenant?.forecourtUnitPriceDecimalPlaces : '1.3-3'}`
          );
        }
        return formatNumber(
          value, 
          this.locale, 
          `${tenant?.amountDecimalPlaces ? '1.' + tenant?.amountDecimalPlaces + '-' + tenant?.amountDecimalPlaces : '1.2-2'}`
        );
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        columnConfig?.formatType === FormatType.CURRENCY
      ) {
         const decimals = tenant?.amountDecimalPlaces;
          const pattern = decimals ? `1.${decimals}-${decimals}` : '1.2-2';
          const formatted = formatNumber(value, this.locale, pattern);
          const symbol = tenant?.currency?.symbol || "";
          return symbol ? `${formatted} ${symbol}` : formatted;
      }
      if (
        (columnConfig?.fieldType === FieldType.NUMBER ||
          columnConfig?.fieldType === FieldType.CHIP_NUMBER) &&
        !isNaN(value)
      ) {
        return value;
      }

      //Text fields
      if (
        columnConfig?.fieldType === FieldType.STRING ||
        columnConfig?.fieldType === FieldType.CHIP_STRING
      ) {
        return value;
      }
    }
  }

  checkFuelProduct(columnElement: any) {
    return columnElement.isFuel;
  }
}
